import { mergeClasses } from "@fluentui/react-components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Navigate, useLoaderData, useNavigate } from "react-router-dom";
import { LoadingContainer } from "../../components/Loading";
import { useAuth } from "../../hooks/useAuth";
import { authApi } from "../../services/auth";
import { ServerError } from "../../services/types";
import { usersApi } from "../../services/users";
import { resetAppSlice } from "../../store/slices/appsSlice";
import { resetGlobalState } from "../../store/slices/globalSlice";
import { openDialog } from "../../utils/helpers";
import { HelpMessage } from "./components/HelpMessage";
import { LoginForm } from "./components/LoginForm";
import { WelcomeSection } from "./components/WelcomeSection";
import { signInSchema } from "./signInSchema";
import { useStyles } from "./styles";

export const AuthenticationApp = () => {
  const styles = useStyles();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<string | undefined>(undefined);
  const { token, ssoProvidersList } = useLoaderData() as any;
  const isAzureEnabled = ssoProvidersList?.some((provider) => provider.name === "azure");
  const azureURL = ssoProvidersList?.find((provider) => provider.name === "azure")?.url;

  const [loginService] = authApi.useLoginMutation();
  const dialogRef = useRef<Office.Dialog | undefined>(undefined);
  const { loginWithAzure } = useAuth();

  useEffect(() => {
    // Show SSO error if present
    const ssoError = sessionStorage.getItem("ssoError");
    if (ssoError) {
      setErrors(ssoError);
      sessionStorage.removeItem("ssoError");
    }
    if (!token) {
      dispatch(resetGlobalState());
      dispatch(resetAppSlice());
      dispatch(usersApi.util.resetApiState());
    }
  }, [dispatch, token]);

  const methods = useForm({
    resolver: yupResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const {
    formState: { isSubmitting, isDirty },
  } = methods;

  const onSubmit = async (values: any) => {
    const { email, password } = values;
    await loginService({ username: email, password })
      .unwrap()
      .then(() => {
        setErrors(null);
        navigate("/");
      })
      .catch((error: ServerError) => {
        if (error.message.includes("Account locked")) {
          setErrors("Account locked");
        } else {
          setErrors("Invalid credentials");
        }
      });
  };

  if (token) {
    return <Navigate to={"/"} replace />;
  }

  const onLoginWithAzureClick = useCallback(() => {
    if (isAzureEnabled && azureURL) {
      // Detect if running inside Office.js
      if (window.window.Office && window.Office.context && window.Office.context.ui) {
        openDialog(azureURL, dialogRef, async (args: any) => {
          try {
            const data = JSON.parse(args.message);
            if (data.code) {
              await loginWithAzure(data.code);
              dialogRef.current?.close();
            } else if (data.error) {
              setErrors(data.error);
              dialogRef.current?.close();
            }
          } catch (e) {
            setErrors("Authentication failed");
            dialogRef.current?.close();
          }
        });
      } else {
        // Not in Office: redirect browser to Azure SSO
        window.location.href = azureURL;
      }
    }
  }, [isAzureEnabled, azureURL, loginWithAzure]);

  return (
    <LoadingContainer
      className={mergeClasses(styles.flexColumnStyle, styles.authContainer)}
      isLoading={isSubmitting}
      size="extra-large"
      label={"LOADING"}
      labelPosition="below"
    >
      <FormProvider {...methods}>
        <div className={mergeClasses(styles.flexColumnStyle, styles.authContainer, styles.fullWidth)}>
          <WelcomeSection isAzureEnabled={isAzureEnabled} onLoginWithAzureClick={onLoginWithAzureClick} />
          <LoginForm onSubmit={onSubmit} errors={errors} isDirty={isDirty} />
          <HelpMessage />
        </div>
      </FormProvider>
    </LoadingContainer>
  );
};
